import React from 'react';
import { Link } from 'react-router-dom';
import { entityPath, type EntityKind } from '../utils/entityRoutes';
import { recordLabel } from '../utils/recordId';
import type { ResolvedCandidate } from '../services/recordRefResolver';
import '../styles/components/RouteRefNotice.css';

type Props =
  | { kind: 'project'; reason: 'unavailable' | 'switch-failed' }
  | { kind: 'ambiguous'; segment: string; candidates: ResolvedCandidate[] };

/** Full-page message for an address that cannot open yet: another project that is not available, or a reference two records share. */
export default function RouteRefNotice(props: Props) {
  if (props.kind === 'project') {
    const failed = props.reason === 'switch-failed';
    return (
      <main className="route-ref-notice" aria-labelledby="route-ref-title">
        <h1 id="route-ref-title" className="route-ref-notice__title">
          {failed ? 'Could not open that project' : 'That project is not available to you'}
        </h1>
        <p className="route-ref-notice__text">
          {failed
            ? 'The link points to a different project and switching to it did not work. Check your connection and try the link again.'
            : 'This link points to a project that is not in your account. Ask the person who sent it to share the project with you.'}
        </p>
        <Link className="route-ref-notice__link" to="/projects">Go to your projects</Link>
      </main>
    );
  }
  return (
    <main className="route-ref-notice" aria-labelledby="route-ref-title">
      <h1 id="route-ref-title" className="route-ref-notice__title">More than one record matches {props.segment}</h1>
      <p className="route-ref-notice__text">Pick the one you meant.</p>
      <ul className="route-ref-notice__list">
        {props.candidates.map(c => {
          const kind = c.kind as EntityKind;
          const to = entityPath(kind, c.id);
          const label = c.ref ?? recordLabel(kind, c.id);
          return (
            <li key={c.id} className="route-ref-notice__item">
              {to ? <Link className="route-ref-notice__link" to={to}>{label}</Link> : <span>{label}</span>}
            </li>
          );
        })}
      </ul>
    </main>
  );
}
